/* ------------------------------------------------------------------ *
 * features/applications/BulkAdmit.tsx
 *
 * Cohort admission for a start-of-year intake. Select approved applications
 * (filtered by year + form), and admit them in one pass — ENROL ONLY:
 *   • resolves each applicant's family (auto-matches an existing family by
 *     guardian, groups siblings within the batch into one new family, or
 *     flags an ambiguous match for individual review),
 *   • creates the student record with the one-shot development-fee marker
 *     (developmentFeeFor(band)) so the next billing run charges it once,
 *   • stamps the application admitted (non-destructive).
 *
 * No invoices are raised here — the normal billing run then invoices the
 * whole intake at once, with the instalment schedule + dev-fee line.
 *
 * The per-student admit path mirrors AdmitWizard; the difference is no
 * joining invoice, automatic family resolution, and the dev-fee marker.
 * ------------------------------------------------------------------ */

import { useMemo, useState } from 'react';
import {
  Users, UserPlus, Search, CheckCircle2, AlertTriangle, X, Loader2, GraduationCap,
} from 'lucide-react';
import { GradientHero, SoftCard, StatChip, glassFieldClass } from '@/features/finance/ui';
import { Button } from '@/components/ui/primitives';
import { useData } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { db, setDoc, addDoc, updateDoc } from '@/lib/firebase';
import { arrayUnion, collection, doc } from 'firebase/firestore';
import { COLLECTIONS } from '@/contexts/DataContext';
import { formBandForForm, developmentFeeFor, fmtPula } from '@/lib/financeCore';
import { getNextAdmissionNumber } from '@/lib/admissions';
import { auditFromContext } from '@/lib/audit';
import { SCHOOL } from '@/lib/school.config';
import type {
  ApplicationRecord, Student, FamilyPrimaryContact, ParentContact, AdmissionsData, Guardian, SchoolClass,
} from '@/types';

type Props = { applications: ApplicationRecord[]; onClose: () => void };

const norm = (s: string | undefined | null) => String(s ?? '').trim().toLowerCase();

function primaryGuardian(app: ApplicationRecord): Guardian | undefined {
  const gs = Array.isArray(app.guardians) ? app.guardians : [];
  return gs.find((g) => g.isPrimaryContact) ?? gs[0];
}

/** Identity key for grouping batch siblings into one new family. */
function guardianKey(g: Guardian | undefined): string {
  if (!g) return '';
  return `${norm(g.fullName)}|${norm(g.email) || norm(g.whatsapp) || norm(g.phone)}`;
}

type FamilyResolution =
  | { kind: 'existing'; familyId: string; familyName: string }
  | { kind: 'new' }
  | { kind: 'review'; count: number };

export default function BulkAdmit({ applications, onClose }: Props) {
  const acad = useData();
  const { userData } = useAuth();

  // Pool: approved, not yet admitted.
  const pool = useMemo(
    () => applications.filter((a) => a.status === 'approved' && !a.admittedStudentId),
    [applications],
  );

  const yearOptions = useMemo(() => {
    const s = new Set<string>(pool.map((a) => a.applyingForYear));
    SCHOOL.selectableYears?.forEach((y) => s.add(String(y)));
    return Array.from(s).sort();
  }, [pool]);

  const [year, setYear] = useState<string>(() => {
    const counts: Record<string, number> = {};
    for (const a of pool) counts[a.applyingForYear] = (counts[a.applyingForYear] ?? 0) + 1;
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? String(SCHOOL.academicYearDefault ?? '');
  });
  const [form, setForm] = useState<string>('all');
  const [targetClassId, setTargetClassId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [done, setDone] = useState<{ admitted: number; newFamilies: number; devFeeTotal: number } | null>(null);

  const formOptions = useMemo(() => {
    const s = new Set<string>(pool.filter((a) => a.applyingForYear === year).map((a) => a.applyingForForm));
    return Array.from(s).sort();
  }, [pool, year]);

  const classesForForm = useMemo(
    () => acad.classes.filter((c) => form === 'all' || c.form === form),
    [acad.classes, form],
  );

  // Resolve a family for an application against existing families.
  const resolveFamily = useMemo(() => {
    return (app: ApplicationRecord): FamilyResolution => {
      const g = primaryGuardian(app);
      if (!g) return { kind: 'new' };
      const gname = norm(g.fullName);
      const gmail = norm(g.email);
      const gwa = norm(g.whatsapp) || norm(g.phone);
      const matches = acad.families.filter((f) => {
        const pc = f.primaryContact;
        if (!pc) return false;
        const nameHit = norm(pc.name) === gname && gname.length > 0;
        const contactHit =
          (gmail && norm(pc.email) === gmail) || (gwa && norm(pc.whatsapp) === gwa);
        return nameHit && (contactHit || (!gmail && !gwa));
      });
      if (matches.length === 1) return { kind: 'existing', familyId: matches[0].id, familyName: matches[0].name };
      if (matches.length > 1) return { kind: 'review', count: matches.length };
      return { kind: 'new' };
    };
  }, [acad.families]);

  const classFor = (app: ApplicationRecord): SchoolClass | undefined => {
    const assigned = app.decisions?.assignedClassId;
    if (assigned && acad.classById?.(assigned)) return acad.classById(assigned);
    if (targetClassId && acad.classById?.(targetClassId)) return acad.classById(targetClassId);
    // last resort: a single class matching the form
    const byForm = acad.classes.filter((c) => c.form === app.applyingForForm);
    return byForm.length === 1 ? byForm[0] : undefined;
  };

  const rows = useMemo(() => {
    const q = norm(search);
    return pool
      .filter((a) => a.applyingForYear === year && (form === 'all' || a.applyingForForm === form))
      .filter((a) => !q || norm(`${a.firstName} ${a.surname} ${a.referenceNumber}`).includes(q))
      .map((a) => {
        const fam = resolveFamily(a);
        const cls = classFor(a);
        const blocked = fam.kind === 'review' || !cls;
        const blockReason = fam.kind === 'review' ? `Matches ${fam.count} families` : !cls ? 'No class assigned' : '';
        return { app: a, fam, cls, blocked, blockReason };
      })
      .sort((a, b) => `${a.app.surname} ${a.app.firstName}`.localeCompare(`${b.app.surname} ${b.app.firstName}`));
  }, [pool, year, form, search, resolveFamily, targetClassId, acad.classById, acad.classes]);

  const selectableIds = useMemo(() => rows.filter((r) => !r.blocked).map((r) => r.app.id), [rows]);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  const toggleAll = () => {
    setSelected(() => (allSelected ? new Set() : new Set(selectableIds)));
  };
  const toggle = (id: string) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const summary = useMemo(() => {
    const chosen = rows.filter((r) => selected.has(r.app.id) && !r.blocked);
    const newFamilies = new Set(chosen.filter((r) => r.fam.kind === 'new').map((r) => guardianKey(primaryGuardian(r.app)))).size;
    const devFeeTotal = chosen.reduce((a, r) => a + developmentFeeFor(formBandForForm(r.app.applyingForForm)), 0);
    return { count: chosen.length, newFamilies, devFeeTotal, blocked: rows.filter((r) => r.blocked).length };
  }, [rows, selected]);

  async function commit() {
    const chosen = rows.filter((r) => selected.has(r.app.id) && !r.blocked);
    if (chosen.length === 0 || busy) return;
    setBusy(true);
    try {
      const now = new Date().toISOString();
      // Local accumulators so admission numbers + sibling families are unique.
      const created: { admissionNumber: string }[] = [];
      const newFamilyByGuardian = new Map<string, string>();
      let admitted = 0;
      const devFeeTotal = summary.devFeeTotal;

      for (let i = 0; i < chosen.length; i++) {
        const { app, fam, cls } = chosen[i];
        setProgress(`Admitting ${i + 1} of ${chosen.length} — ${app.firstName} ${app.surname}…`);
        const newId = doc(collection(db, COLLECTIONS.students)).id;
        const admissionNumber = getNextAdmissionNumber([...(acad.students as Student[]), ...(created as Student[])]);
        created.push({ admissionNumber });

        const g = primaryGuardian(app);
        const band = formBandForForm(app.applyingForForm);

        // ---- family ----
        let familyId: string;
        if (fam.kind === 'existing') {
          familyId = fam.familyId;
          await updateDoc(doc(db, COLLECTIONS.families, familyId), {
            studentIds: arrayUnion(newId),
            updatedAt: now,
          });
        } else {
          const key = guardianKey(g);
          const existingBatch = key ? newFamilyByGuardian.get(key) : undefined;
          if (existingBatch) {
            familyId = existingBatch;
            await updateDoc(doc(db, COLLECTIONS.families, familyId), {
              studentIds: arrayUnion(newId),
              updatedAt: now,
            });
          } else {
            const primary: FamilyPrimaryContact = {
              name: g?.fullName || `${app.firstName} ${app.surname}`,
              email: g?.email || undefined,
              whatsapp: g?.whatsapp || g?.phone || undefined,
              consentToSend: g?.consentToSend ?? false,
            };
            const famRef = await addDoc(collection(db, COLLECTIONS.families), {
              name: `${app.surname} Family`,
              studentIds: [newId],
              primaryContact: primary,
              createdBy: userData?.uid ?? '',
              createdAt: now,
              updatedAt: now,
            });
            familyId = famRef.id;
            if (key) newFamilyByGuardian.set(key, familyId);
          }
        }

        // ---- student (enrol only + dev-fee marker) ----
        const parentContact: ParentContact | undefined = g
          ? {
              guardianName: g.fullName || '',
              email: g.email || '',
              whatsapp: g.whatsapp || g.phone || '',
              consentToSend: g.consentToSend ?? false,
            }
          : undefined;

        const admissions: AdmissionsData = {
          firstName: app.firstName,
          surname: app.surname,
          nationality: app.nationality,
          homeLanguage: app.homeLanguage || '',
          religion: app.religion || '',
          address: app.address,
          guardians: Array.isArray(app.guardians) ? app.guardians : [],
          medical: app.medical,
          emergencyContactName: app.emergencyContactName || '',
          emergencyContactPhone: app.emergencyContactPhone || '',
          previousSchool: app.previousSchool || '',
          boardingStatus: app.boardingStatus || 'day',
          dateOfAdmission: now.slice(0, 10),
          notes: app.notes || '',
          updatedAt: now,
          updatedBy: userData?.uid ?? '',
        };

        const studentDoc: Student = {
          id: newId,
          fullName: `${app.firstName} ${app.surname}`.trim(),
          currentClass: cls!.id,
          gender: app.gender,
          dateOfBirth: app.dateOfBirth,
          admissionNumber,
          subjects: [],
          subjectTeachers: {},
          parentContact,
          admissions,
          familyId,
          pendingDevelopmentFee: developmentFeeFor(band),
          createdAt: now,
          createdBy: userData?.uid ?? '',
          updatedAt: now,
          updatedBy: userData?.uid ?? '',
        };
        await setDoc(doc(db, COLLECTIONS.students, newId), studentDoc as unknown as Record<string, unknown>);

        // ---- stamp the application (non-destructive) ----
        await updateDoc(doc(db, COLLECTIONS.applications, app.id), {
          admittedStudentId: newId,
          admittedAt: now,
          admittedBy: userData?.uid ?? '',
          status: 'approved',
          updatedAt: now,
        });
        admitted++;
      }

      auditFromContext(userData, 'application_admitted', 'admissions', {
        targetId: 'bulk',
        targetType: 'student',
        targetName: `Bulk intake — ${admitted} students`,
        details: { admitted, year, form, newFamilies: newFamilyByGuardian.size, devFeeTotal },
      });

      setDone({ admitted, newFamilies: newFamilyByGuardian.size, devFeeTotal });
    } catch (e) {
      console.error('Bulk admit failed', e);
      setProgress('Something went wrong — some students may have been admitted. Close and re-open to see the remaining applications.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm overflow-y-auto p-4">
      <div className="mx-auto max-w-4xl my-6 rounded-2xl bg-white dark:bg-gray-900 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <h2 className="font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <Users size={18} /> Bulk admit
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-5">
          {done ? (
            <SoftCard hover={false}>
              <div className="flex items-start gap-3">
                <CheckCircle2 size={22} className="text-emerald-600 shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">Admitted {done.admitted} student{done.admitted === 1 ? '' : 's'}.</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    {done.newFamilies} new famil{done.newFamilies === 1 ? 'y' : 'ies'} created. A development fee of {fmtPula(done.devFeeTotal)} in total is queued and will appear on each student's first invoice.
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                    Next: open <span className="font-medium">Billing run</span> for the term to invoice the whole intake at once.
                  </p>
                  <Button className="mt-3" onClick={onClose}>Done</Button>
                </div>
              </div>
            </SoftCard>
          ) : (
            <>
              <GradientHero
                icon={GraduationCap}
                title="Admit a cohort"
                subtitle="Enrol approved applicants in one pass. Invoices are raised later by the billing run."
              />

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatChip label="Ready to admit" value={summary.count} icon={UserPlus} />
                <StatChip label="New families" value={summary.newFamilies} icon={Users} />
                <StatChip label="Need review" value={summary.blocked} icon={AlertTriangle} tone={summary.blocked ? 'warn' : undefined} />
                <StatChip label="Dev fee total" value={fmtPula(summary.devFeeTotal)} icon={CheckCircle2} />
              </div>

              <SoftCard hover={false}>
                <div className="flex flex-wrap gap-3 items-end">
                  <label className="block">
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Year</span>
                    <select value={year} onChange={(e) => { setYear(e.target.value); setSelected(new Set()); }} className={glassFieldClass('py-2')}>
                      {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Form</span>
                    <select value={form} onChange={(e) => { setForm(e.target.value); setTargetClassId(''); setSelected(new Set()); }} className={glassFieldClass('py-2')}>
                      <option value="all">All forms</option>
                      {formOptions.map((f) => <option key={f} value={f}>Form {f}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Assign to class</span>
                    <select value={targetClassId} onChange={(e) => setTargetClassId(e.target.value)} className={glassFieldClass('py-2')}>
                      <option value="">Use class from approval</option>
                      {classesForForm.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </label>
                  <div className="relative flex-1 min-w-[10rem]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / reference…" className={glassFieldClass('py-2 pl-9 w-full text-sm')} />
                  </div>
                </div>
              </SoftCard>

              <SoftCard hover={false}>
                {rows.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No approved applications match. Approve applications first, or change the filters.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 border-b border-gray-100 dark:border-gray-800">
                          <th className="py-2 px-2 w-8">
                            <input type="checkbox" checked={allSelected} onChange={toggleAll} className="rounded" aria-label="Select all" />
                          </th>
                          <th className="py-2 px-2">Applicant</th>
                          <th className="py-2 px-2">Form</th>
                          <th className="py-2 px-2">Family</th>
                          <th className="py-2 px-2 text-right">Dev fee</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(({ app, fam, blocked, blockReason }) => {
                          const band = formBandForForm(app.applyingForForm);
                          return (
                            <tr key={app.id} className={`border-b border-gray-50 dark:border-gray-800/60 ${blocked ? 'opacity-60' : ''}`}>
                              <td className="py-2 px-2">
                                <input
                                  type="checkbox"
                                  disabled={blocked}
                                  checked={selected.has(app.id)}
                                  onChange={() => toggle(app.id)}
                                  className="rounded"
                                  aria-label={`Select ${app.firstName} ${app.surname}`}
                                />
                              </td>
                              <td className="py-2 px-2">
                                <div className="font-medium text-gray-800 dark:text-gray-100">{app.surname}, {app.firstName}</div>
                                <div className="text-xs text-gray-400 font-mono">{app.referenceNumber}</div>
                              </td>
                              <td className="py-2 px-2 text-gray-600 dark:text-gray-300">Form {app.applyingForForm}</td>
                              <td className="py-2 px-2">
                                {fam.kind === 'existing' && <span className="text-xs text-gray-600 dark:text-gray-300">Links to {fam.familyName}</span>}
                                {fam.kind === 'new' && <span className="text-xs text-emerald-600 dark:text-emerald-400">New family</span>}
                                {fam.kind === 'review' && <span className="text-xs text-amber-600 dark:text-amber-400">{blockReason}</span>}
                                {fam.kind !== 'review' && blocked && <span className="text-xs text-amber-600 dark:text-amber-400">{blockReason}</span>}
                              </td>
                              <td className="py-2 px-2 text-right tabular-nums text-gray-600 dark:text-gray-300">{fmtPula(developmentFeeFor(band))}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {summary.blocked > 0 && (
                  <p className="mt-3 text-xs text-amber-600 dark:text-amber-400 flex items-start gap-1.5">
                    <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                    {summary.blocked} application{summary.blocked === 1 ? '' : 's'} need attention (ambiguous family or no class) — admit those one at a time from the application, or pick a target class above.
                  </p>
                )}
              </SoftCard>

              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {busy ? progress : `${summary.count} selected · ${fmtPula(summary.devFeeTotal)} in development fees queued`}
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl dark:text-gray-300 dark:hover:bg-gray-700">Cancel</button>
                  <Button onClick={commit} disabled={busy || summary.count === 0}
                    icon={busy ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />}>
                    {busy ? 'Admitting…' : `Admit ${summary.count}`}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
